/* eslint-disable no-unused-vars */
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { TWC_BASE_KEY, createQueryKey } from "@/api/query-keys"
import { axiosInstance } from "@/api/axios"
import {
  PayableAllowedTypes,
  PayableBaseFieldsKeys,
  PayableConfigType,
  PayableDataPost,
  PayablePeriodDataResponse,
  PayableSetupPostRequest,
  PayableSetUpResponse,
  PayableTotals,
} from "../types"
import { PeriodTypeQuery, PeriodTypeView } from "@/types"
import { parsePayableConfiguration } from "../utils"
import { PayableConfiguration, useTwcStore } from "../store"
import { TWC_TOTALS_KEY } from "./totals"

export const PAYABLE_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`payable`],
})

const PERIOD_DATA_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`payable`, `period-data`],
})

const TOTALS_KEY = TWC_TOTALS_KEY.concat([`payable`])

const getPayableSetup = async (
  financialModelId: string
): Promise<PayableSetUpResponse> => {
  const response = await axiosInstance.get<PayableSetUpResponse>(
    `/financial-models/${financialModelId}/payables/setup/`
  )
  return response.data
}

export const getPayableSetUpQueryOptions = (financialModelId: string) =>
  queryOptions({
    queryKey: PAYABLE_KEY.concat([`setup`]),
    queryFn: () => getPayableSetup(financialModelId),
    select: (data) => parsePayableConfiguration(data),
  })

export const useGetPayableSetup = (financialModelId: string) =>
  useSuspenseQuery(getPayableSetUpQueryOptions(financialModelId))

const updatePayableSetup = async (
  params: PayableSetupPostRequest,
  financialModelId: string
): Promise<PayableConfiguration> => {
  const response = await axiosInstance.post<PayableSetUpResponse>(
    `/financial-models/${financialModelId}/payables/setup`,
    params
  )
  return parsePayableConfiguration(response.data)
}

export const useUpdatePayableSetup = (financialModelId: string) => {
  const queryClient = useQueryClient()
  const { setPayableConfiguration } = useTwcStore.getState()
  return useMutation({
    mutationFn: (params: PayableSetupPostRequest) =>
      updatePayableSetup(params, financialModelId),
    onSuccess: (response) => {
      queryClient.resetQueries({
        queryKey: PAYABLE_KEY,
      })
      queryClient.resetQueries({
        queryKey: PERIOD_DATA_KEY,
      })
      queryClient.resetQueries({
        queryKey: TOTALS_KEY,
      })
      setPayableConfiguration(response)
    },
  })
}

type GETDefaultParams = {
  financialModelId: string
  period_type: PeriodTypeQuery
  view: PeriodTypeView
  setup_id: string
  config_type?: PayableConfigType
}

const getPayablePeriodData = async (
  params: GETDefaultParams
): Promise<PayablePeriodDataResponse> => {
  const response = await axiosInstance.get<PayablePeriodDataResponse>(
    `/financial-models/${params.financialModelId}/payables/data-entry/${params.setup_id}`,
    {
      params: {
        type: params.period_type,
        view: params.view,
        setup_id: params.setup_id,
      },
    }
  )
  return response.data
}

export const useGetPayableQuery = (params: GETDefaultParams) =>
  useSuspenseQuery({
    queryKey: PERIOD_DATA_KEY.concat([
      params.financialModelId,
      params.period_type,
      params.view,
      params.setup_id,
    ]),
    queryFn: () => getPayablePeriodData(params),
  })

type UpdatePayablePeriodDataParams = {
  financialModelId: string
  setup_id: string
  data: Partial<PayableDataPost>
}

export const updatePayablePeriodData = async (
  params: UpdatePayablePeriodDataParams
) => {
  const { financialModelId, data, setup_id } = params
  const dataToSend = {
    ...data,
    year: Number(data.year),
    month: Number(data.month),
  }
  const response = await axiosInstance.post<PayablePeriodDataResponse>(
    `/financial-models/${financialModelId}/payables/data-entry/${setup_id}`,
    dataToSend
  )
  return response.data
}

export const useUpdatePayablePeriodData = ({
  financialModelId,
  setup_id,
}: {
  financialModelId: string
  setup_id: string
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<PayableDataPost>) =>
      updatePayablePeriodData({ financialModelId, data, setup_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId]),
      })
      queryClient.invalidateQueries({
        queryKey: TOTALS_KEY.concat([financialModelId]),
      })
    },
  })
}

type SwitchPayableColumnTypeParams = {
  setup_id: string
  financialModelId: string
  input_type: PayableAllowedTypes
  column_name: PayableBaseFieldsKeys
  month?: number
  year?: number
  product_id?: string | null
  cost_category_id?: string | null
}

const switchPayableColumnType = async (
  params: SwitchPayableColumnTypeParams
) => {
  const { financialModelId, column_name, ...rest } = params
  const response = await axiosInstance.post<PayablePeriodDataResponse>(
    `/financial-models/${financialModelId}/payables/config/${column_name}`,
    undefined,
    { params: rest }
  )
  return response.data
}

type SwitchPayableQueryColumnTypeParams = Omit<
  SwitchPayableColumnTypeParams,
  "financialModelId"
>

export const useSwitchPayableColumnType = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchPayableQueryColumnTypeParams) =>
      switchPayableColumnType({
        ...params,
        financialModelId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId]),
      })
      queryClient.invalidateQueries({
        queryKey: TOTALS_KEY.concat([financialModelId]),
      })
    },
  })
}

const getPayableTotals = async (
  params: Omit<GETDefaultParams, "setup_id">
): Promise<PayableTotals> => {
  const response = await axiosInstance.get<PayableTotals>(
    `/financial-models/${params.financialModelId}/payables/data-entry/totals`,
    {
      params: {
        type: params.period_type,
        view: params.view,
        config_type: params.config_type,
      },
    }
  )
  return response.data
}

export const useGetPayableTotals = (
  params: Omit<GETDefaultParams, "setup_id">
) =>
  useSuspenseQuery({
    queryKey: TOTALS_KEY.concat([
      params.financialModelId,
      params.period_type,
      params.view,
    ]),
    queryFn: () => getPayableTotals(params),
  })
